import React from 'react';

import { Box, Tooltip, Typography, alpha } from '@mui/material';

import { TccPricingRecommendation } from './TccPricingProvider';
import { money, pct } from './logic';
import { insuranceBreakageTooltip } from './moneyNotes';
import { packageBreakdown, packageExclusionNotes, packageLabel, packageMoney } from './packageRows';
import { tcc } from './palette';
import { priceLevel } from './priceFormatting';
import { lastPriceAction } from './reviewLogic';
import { CommandDeparture, CommandPackage, CostBasis } from './types';

const departureColumns = [
  'Package',
  'Travellers',
  'TTV',
  'Tour cost (incl. ICS)',
  'Hotel and add-ons',
  'Margin',
  'Margin %',
];
const mobile = '@media (max-width: 767px)';
const gridColumns = 'minmax(0, 2.8fr) minmax(0, 0.7fr) repeat(4, minmax(0, 1fr)) minmax(0, 0.7fr)';
const hatch = (colour: string, opacity = 0.15) =>
  `repeating-linear-gradient(135deg, transparent, transparent 4px, ${alpha(colour, opacity)} 4px, ${alpha(colour, opacity)} 6px)`;

export default function DeparturePackages({
  packages,
  basis,
  tour = false,
  departure,
}: {
  packages: Array<CommandPackage & { departures?: number }>;
  basis: CostBasis;
  tour?: boolean;
  departure?: CommandDeparture;
}) {
  const columns = tour
    ? [
        'Package',
        'Departures',
        'Travellers',
        'Share of Travellers',
        'TTV',
        'Avg Price / Traveller',
        'Margin',
        'Margin %',
      ]
    : departureColumns;
  const layout = tour ? 'minmax(0, 2.8fr) repeat(7, minmax(0, 1fr))' : gridColumns;
  const breakdown = packageBreakdown(packages);
  const split = breakdown.filter(({ row }) => row.type !== 'cancellation');
  const totalPax = split.reduce((sum, { row }) => sum + row.pax, 0);
  const legend = split.map(({ row, legendLabel }) => `${legendLabel} ${row.pax}`).join(' · ');

  return (
    <Box
      sx={{
        mx: 1.5,
        p: 2,
        minWidth: 0,
        bgcolor: (theme) => theme.palette.action.hover,
        borderTop: '1px solid',
        borderColor: 'divider',
        [mobile]: { mx: 0.5, p: 1.5 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          [mobile]: { flexDirection: 'column', alignItems: 'stretch' },
        }}
      >
        <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          {tour ? 'Booked packages in the summary selection' : 'Packages on this departure · booked so far'}
        </Typography>
        <Box sx={{ width: 280, maxWidth: '100%', [mobile]: { width: '100%' } }}>
          <Box
            role="img"
            aria-label={`Travellers by package: ${legend}. Hatching marks travellers not included in margin.`}
            sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.selected' }}
          >
            {split.map(({ row, colour, excludedPax }, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', width: `${totalPax > 0 ? (row.pax / totalPax) * 100 : 0}%`, minWidth: 0 }}
              >
                <Box sx={{ flex: Math.max(0, row.pax - excludedPax), bgcolor: colour }} />
                <Box
                  aria-label={`${row.name}: ${excludedPax} excluded travellers`}
                  sx={{ flex: excludedPax, bgcolor: alpha(colour, 0.12), backgroundImage: hatch(colour) }}
                />
              </Box>
            ))}
          </Box>
          <Typography
            component="div"
            sx={{
              mt: 0.75,
              fontSize: 11,
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
              [mobile]: { textAlign: 'left' },
            }}
          >
            {split.map(({ row, colour, legendLabel }, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {' '}
                    ·{' '}
                  </Box>
                )}
                <Box component="span" sx={{ color: colour }}>
                  {legendLabel} {row.pax}
                </Box>
              </React.Fragment>
            ))}
          </Typography>
        </Box>
      </Box>
      <Box role="table" aria-label="Departure packages" sx={{ minWidth: 0 }}>
        <Box
          role="row"
          sx={{
            display: 'grid',
            gridTemplateColumns: layout,
            gap: 1,
            px: 1.5,
            mb: 0.75,
            borderLeft: '4px solid transparent',
            [mobile]: { display: 'none' },
          }}
        >
          {columns.map((label, index) => (
            <Box
              role="columnheader"
              key={label}
              sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textAlign: index ? 'right' : 'left' }}
            >
              {label}
            </Box>
          ))}
        </Box>
        {breakdown.map(({ row, colour, legendLabel }, index) => {
          const values = packageMoney(row, basis);
          const notes = packageExclusionNotes(row);
          const excluded = row.type !== 'cancellation' && row.margin_pax === 0;
          const excludedTtv = row.ttv - row.margin_ttv;
          const marginColour = values?.margin < 0 ? tcc.redText : values?.margin > 0 ? tcc.greenText : 'text.primary';
          const cells = tour
            ? [
                packages[index].departures,
                row.type === 'cancellation' ? '—' : row.pax,
                row.type === 'cancellation' || !totalPax ? '—' : pct(row.pax / totalPax),
                money(values?.ttv),
                money(row.pax > 0 ? row.ttv / row.pax : null),
                money(values?.margin),
                pct(values?.marginPct),
              ]
            : [
                row.type === 'cancellation' ? '—' : row.pax,
                money(excluded ? null : row.margin_ttv),
                money(values?.tourCost),
                !values || (row.type === 'base' && values.addonCost === 0) ? '—' : money(values.addonCost),
                money(values?.margin),
                pct(values?.marginPct),
              ];
          return (
            <Box
              role="row"
              key={`${row.type}-${row.package_opp_id ?? index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: layout,
                gap: 1,
                alignItems: 'center',
                px: 1.5,
                py: 1.5,
                mb: 1,
                minWidth: 0,
                bgcolor: 'background.paper',
                backgroundImage: excluded ? hatch(colour, 0.06) : 'none',
                border: '1px solid',
                borderStyle: excluded ? 'dashed' : 'solid',
                borderColor: 'divider',
                borderLeft: `4px solid ${colour}`,
                borderRadius: 1,
                [mobile]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 1.5 },
              }}
            >
              <Box role="cell" data-label="Package" sx={{ minWidth: 0, [mobile]: { gridColumn: '1 / -1' } }}>
                {row.type !== 'cancellation' && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      color: colour,
                      bgcolor: alpha(colour, 0.1),
                      borderRadius: 0.5,
                      px: 0.75,
                      py: 0.25,
                      mr: 0.75,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {row.type === 'base' ? 'TOUR ONLY' : legendLabel.toUpperCase()}
                  </Box>
                )}
                <Tooltip title={row.type === 'cancellation' ? insuranceBreakageTooltip : row.name}>
                  <Box component="span" tabIndex={0} sx={{ fontSize: 12, fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {packageLabel(row)}
                  </Box>
                </Tooltip>
                {!tour && row.type !== 'cancellation' && row.list_price_now_aud !== undefined && (
                  <Typography variant="caption" display="block">
                    Now {money(row.list_price_now_aud)} ·{' '}
                    {row.price_level_mixed
                      ? 'mixed'
                      : row.price_level_now == null
                        ? 'price level missing'
                        : priceLevel(row.price_level_now)}
                  </Typography>
                )}
                {!tour && lastPriceAction(row.last_price_action) && (
                  <Typography variant="caption" display="block">
                    {lastPriceAction(row.last_price_action)}
                  </Typography>
                )}
                {departure && row.type !== 'cancellation' && row.type !== 'base' && (
                  <TccPricingRecommendation departure={departure} pkg={row} />
                )}
                {notes.map((note) => (
                  <Typography key={note} sx={{ mt: 0.5, fontSize: 11, color: tcc.amberText }}>
                    {note}
                  </Typography>
                ))}
              </Box>
              {cells.map((value, cellIndex) => (
                <Box
                  role="cell"
                  data-label={columns[cellIndex + 1]}
                  key={columns[cellIndex + 1]}
                  sx={{
                    minWidth: 0,
                    textAlign: 'right',
                    fontSize: 12,
                    fontVariantNumeric: 'tabular-nums',
                    color: cellIndex >= (tour ? 5 : 4) ? marginColour : 'text.primary',
                    [mobile]: {
                      '&::before': {
                        content: 'attr(data-label)',
                        display: 'block',
                        mb: 0.5,
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'text.secondary',
                      },
                    },
                  }}
                >
                  {value}
                  {cellIndex === (tour ? 3 : 1) && excludedTtv > 0 && (
                    <Typography sx={{ mt: 0.5, fontSize: 11, color: 'text.secondary' }}>
                      {excluded ? '' : '+'}
                      {money(excludedTtv)} not in margin
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
